/**
 * useVisibilityAwareInterval · F5
 *
 * Drop-in replacement for `setInterval` in React that:
 *   1. Pauses the callback while the tab is hidden (`document.hidden`), so
 *      background tabs stop hammering the backend.
 *   2. Fires immediately on visibility-return if the tab was hidden longer
 *      than the interval (so users see fresh data as soon as they refocus).
 *   3. Optionally applies an exponential-backoff when a caller-supplied
 *      `idleCheck` returns true (e.g. "no open tasks" → poll less often).
 *
 * Non-breaking: the callback signature and semantics are identical to
 * `setInterval`; the only behavioural change is *fewer* invocations when
 * the user isn't looking.
 *
 * Usage:
 *   useVisibilityAwareInterval(refresh, 45_000);
 *   useVisibilityAwareInterval(refresh, 45_000, { idleCheck: () => count === 0, idleMultiplier: 4 });
 */
import { useEffect, useRef } from 'react';

export default function useVisibilityAwareInterval(callback, delayMs, options = {}) {
  const { idleCheck, idleMultiplier = 4, enabled = true } = options;
  const savedCallback = useRef(callback);
  const savedIdleCheck = useRef(idleCheck);
  const lastRunAt = useRef(Date.now());

  useEffect(() => { savedCallback.current = callback; }, [callback]);
  useEffect(() => { savedIdleCheck.current = idleCheck; }, [idleCheck]);

  useEffect(() => {
    if (!enabled || !delayMs) return undefined;

    let timerId = null;

    const tick = async () => {
      lastRunAt.current = Date.now();
      try { await savedCallback.current?.(); } catch { /* swallow */ }
      schedule();
    };

    const schedule = () => {
      const idle = typeof savedIdleCheck.current === 'function' && savedIdleCheck.current();
      const nextDelay = idle ? delayMs * idleMultiplier : delayMs;
      timerId = setTimeout(tick, nextDelay);
    };

    const onVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        // Tab hidden - cancel any pending tick.
        if (timerId) { clearTimeout(timerId); timerId = null; }
      } else {
        // Tab visible again - if we've been away longer than the delay,
        // fire immediately so the UI shows fresh data.
        const stale = Date.now() - lastRunAt.current;
        if (timerId) { clearTimeout(timerId); timerId = null; }
        if (stale >= delayMs) {
          tick();
        } else {
          schedule();
        }
      }
    };

    // First run + kick off polling.
    tick();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return () => {
      if (timerId) clearTimeout(timerId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    // We intentionally exclude the callback/idleCheck from deps - they're
    // captured via refs so the interval isn't torn down every render.
  }, [delayMs, enabled, idleMultiplier]);
}
